import {
  MASTER_KEYS,
  STORES_MASTER_KEYS,
  type MasterKey,
} from "./masters/keys";
import type { PackagingReportKey } from "./packaging-reports";
import type { QualityReportSlug } from "./quality-reports";
import type { StockReportKey } from "./stock-reports";
import type { CswReportKey, WarehouseReportKey } from "./warehouse-reports";

// The key list is part of the permission contract now — the master codes are derived from it — so
// it is reachable from the package root alongside them, not only under the `masters` namespace.
export { MASTER_KEYS, type MasterKey } from "./masters/keys";

/**
 * Permission codes — a closed enum in code (no permission table). Roles map to sets of these;
 * per-user overrides are grant-only. `access_all_facilities` is the head-office bypass of facility
 * scoping. Separation of duties per C23 is expressed by which roles receive which codes.
 *
 * Every code here is enforced by a real call site. `scripts/rbac-scan.ts` fails CI in both
 * directions: a code no `requirePermission`/`can` caller names, or a caller naming a code that is
 * not in this list. That is what keeps the admin permission tree honest — the tree is generated
 * from what the server actually enforces, so a checkbox cannot exist for a capability nobody can
 * exercise.
 *
 * A new module therefore ships its permissions in the same commit that ships its routes. Do not
 * add codes here ahead of the screens that enforce them.
 */
const BASE_PERMISSIONS = [
  // The dashboard, as its own screen. Deliberately not `document.view`: a login narrowed to a few
  // forms needs document.view to work them, and has no business with plant-wide numbers.
  "dashboard.view",
  // One code per Dashboard tab (user, 2026-10-05): the tab shows, and its numbers load, only for a
  // login holding it as well as dashboard.view. Before this the tabs borrowed module codes, so
  // three of them were all `document.view` and could not be told apart.
  "dashboard.overview",
  "dashboard.gate",
  "dashboard.arrival",
  "dashboard.quality",
  "dashboard.processing",
  "dashboard.packaging",
  // The Stores tab (user, 2026-10-14): stock, reorder, work waiting and the period's movement, for
  // the Stores Manager. Its numbers drill into Stores screens, so it also needs stores.view.
  "dashboard.stores",
  // The QA Supervisor's and the QA Manager's dashboards (user, 2026-10-20; docs/designs/
  // qa-dashboard.md), one per role (2026-10-21). Hand-granted, never derived from a module code:
  // qc.view would hand every QA login the Manager's view, and qc.verify every Manager the
  // Supervisor's. They replaced the QC Supervisor tab, whose dashboard.qc_supervisor code retired.
  "dashboard.qa_supervisor",
  "dashboard.qa_manager",
  // The Lab Manager's germination dashboard (user, 2026-10-22): UT samples SRF-wise, packets
  // received and counts due today. Hand-granted like the two above.
  "dashboard.lab_manager",
  // The GOT Manager's dashboard (user, 2026-11-03): every GOT sample's stage, what is overdue and the
  // period's results. Hand-granted to the roles holding got.review; its numbers drill into GOT screens.
  "dashboard.got",
  // documents (all types share the engine; per-form grants live in `role_form_permissions`)
  "document.view",
  "document.create",
  "document.edit",
  "document.submit",
  "document.cancel",
  // Previewing the unloading of a truck with bags still short of the declared count, with a reason
  // (user, 2026-09-23). Everyone else is refused: a Stock Transfer In's or S-Capture's declared count
  // is the sender's and cannot be corrected, so a real transit loss needs someone allowed to say so.
  "document.short_handover",
  // E-mailing an arrival to the production personnel from its list row (user, 2026-09-26).
  "document.email",
  // No approve / return_to_draft / discard, and no weighing codes: an arrival is filled, previewed
  // and submitted, and a permission nothing checks is a lie in the admin UI — it lets someone grant
  // a capability that cannot be exercised. SLOC is part of editing the document, so it needs no
  // code of its own.
  // gate (the security desk; separate from the document codes because the guard touches no document)
  "gate.view",
  // Passing in a truck something announced: the Gate inward button on an Inward Pending row.
  "gate.inward",
  // Typing a Gate Pass In for a truck nothing announced: the New Gate Pass In button and its form.
  // Split from `gate.inward` (user, 2026-09-24) so a role can have one button without the other.
  "gate.pass_in",
  // Typing the paper pass number on a Gate Pass In instead of the system's, for a load unloaded at a
  // hired warehouse, field or cold store; the pass then records that unloading location. Whether
  // the switch is forced on, off or left to the user is the role's `pass_in_manual_mode`.
  "gate.pass_in.manual",
  // Destination change on Inward Pending: sending an S-Capture truck to another plant before it
  // reaches the gate. Not part of `gate.inward`: moving a truck off a plant's queue is a desk call.
  "gate.divert",
  // Letting a Stock Transfer truck out at the sending plant: raising and cancelling a Gate Pass Out.
  "gate.outward",
  // The arrival executive's pick, on Inward Done, of which arrival form a Gate Pass In is for.
  // Not part of `gate.inward`: the guard raises the pass but does not know what the load books into.
  "gate.assign_arrival",
  // One code per Gate register tab (user, 2026-10-05; replaced roles.gate_tabs). A hidden tab also
  // stops the work done from it: inward and divert need Inward Pending, Gate Pass In and the arrival
  // pick need Inward Done, the gate pass out needs Outward Pending.
  "gate.tab.all",
  "gate.tab.inward_pending",
  "gate.tab.inward_done",
  "gate.tab.outward_pending",
  "gate.tab.outward_done",
  // qc (the lab gate: manual ch.4.1). One code per handheld QC step (user, 2026-10-13: "role based
  // access to each module"), so a role can sample without collecting, or run Moisture without
  // Germination. They replaced `qc.collect` doing both Generation and Collection, and
  // `qc.enter_results` covering every test and the result (migration 0107 carried the grants over).
  "qc.view",
  // QC Dashboard (user, 2026-10-16): the plant's QC page; QA staff land on it after login.
  "qc.dashboard",
  // The two QC pages that are not part of every QC login's work (user, 2026-10-05): QC Sample
  // Pending, the lab's worklist of samples to draw, and QC Standards, each crop's pass marks. Both
  // opened on `qc.view` before, so a desk holding it for QC Sampling Request got them too.
  "qc.sample_pending",
  "qc.standards",
  "qc.request",
  // Sample Generation: picking the truck's sampler, scanning the bags drawn, the floor slip.
  "qc.generation",
  // Sample Collection: collecting drawn samples onto an SRF (handheld only, user 2026-10-13).
  "qc.collect",
  "qc.moisture",
  "qc.germination",
  // Germination setup (user, 2026-10-23: on the handheld, one lot at a time; on the web from
  // 2026-10-22): the Lab Manager sets up each tray once its analyst scanned the packet; the analyst
  // only counts.
  "qc.germination_setup",
  "qc.pp",
  // QC Result Update: OK, Retest or Fail.
  "qc.result_update",
  // The QC supervisor's check of an analyst's readings: Moisture % verification (QCS p170–171).
  "qc.verify",
  // QC Sample Allocation (user, 2026-10-14): give each sample's Moisture / PP / Germination to an
  // analyst, confirm an SRF's packets were received, and follow every assigned test's progress.
  "qc.allocate",
  // The Allocation Monitor read-only (user, 2026-10-14): the lab's view of every test with the lab,
  // who holds it and how far it has got, for QA roles that do not allocate.
  "qc.allocation_view",
  // Moving an Under Testing lot (seedtrac's SR / SSR, QCS p155–159, QCM p217–220): a Soft Release
  // for a lot whose GOT is not required (QC Supervisor or Manager), a Super Soft Release for one
  // whose GOT is required (QC Manager only). User, 2026-09-30.
  "qc.soft_release",
  "qc.super_soft_release",
  // Guard Samples (user, 2026-10-13; seedtrac Sample Collection report, manual p193–194): where each
  // sample's guard packet is kept once its result is updated, its Discard by date, and its discard.
  "qc.guard_samples",
  // Lot Biography in the Quality menu (user, 2026-10-16): QA reads a lot's whole life without
  // holding inventory.view, which would also open the Inventory and Warehouse menus.
  "qc.lot_biography",
  // Look-up › Quality's packaging utilities, one tick each (user, 2026-10-31): they opened on
  // qc.view alone, so every QC login saw all three. Granted to every role holding qc.view then.
  "qc.dov_calculator",
  "qc.barcode_search",
  "qc.batch_finder",
  // Density data (user, 2026-10-16; seedtrac QCS p174–176): enter a lot's Raw / Condition density
  // weight and review processed Condition lots; the QC Manager's Density Data Edit (QCM p221).
  "qc.density",
  "qc.density_edit",
  // High moisture alert (user, 2026-10-27): the top strip's bell, raised when a Moisture Meter
  // Reading on an arrival sample is above the crop's Normal moisture standard. Arrival Manager + admin.
  "qc.moisture_alert",
  // Reports > Quality (user, 2026-10-29: "show only up to that much the permission is granted"): one
  // code per report, and the code alone opens it; QC View and the Arrival Report no longer do.
  "qc.reports.sample_pending",
  "qc.reports.result_pending",
  // The lab's two daily germination sheets (user, 2026-11-01): 1st Count and Evaluation (Final Count).
  "qc.reports.germ_first_count_list",
  "qc.reports.germ_evaluation_list",
  "qc.reports.substandard",
  "qc.reports.soft_release",
  "qc.reports.super_soft_release",
  "qc.reports.blending",
  "qc.reports.stock",
  "qc.reports.sample_collection",
  "qc.reports.stock_status",
  "qc.reports.density",
  "qc.reports.testing_data",
  "qc.reports.pp_testing",
  "qc.reports.moisture_testing",
  "qc.reports.periodical_arrival",
  // The Key reports' Daily Arrival tiles (that report only) and Periodical Dispatch, ticked the same way.
  "qc.reports.daily_arrival",
  "qc.reports.periodical_dispatch",
  // Orders and Dispatch reports (user, 2026-10-30): one code per report, the code alone opens it.
  "dispatch.reports.pending_orders",
  "dispatch.reports.booked_orders",
  "dispatch.reports.cancelled_orders",
  "dispatch.reports.suspended_orders",
  "dispatch.reports.dc_wise",
  "dispatch.reports.crop_variety",
  "dispatch.reports.cnf_barcodes",
  "dispatch.reports.pack_activity",
  "dispatch.reports.pack_ageing",
  // GOT — Grow-Out Test (docs/designs/got-legacy-map.md): one code per legacy screen. The web
  // desk's: Sample Dispatch (GSDN), Print GOT Sample, Result Update (IN-TERRA / IN-SITU), Review
  // (GOT Status), Final GOT Grade (QCM), Biography, Reports and the GOT Location master. The
  // QC-App's: Sample Receipt, Allocation, the four data steps, Review and Final Submit, BTS.
  // Primary / Intermediate operators (legacy `oprtype`) are expressed by which step codes a login
  // holds: sowing only = Primary, no Final Observation = Intermediate.
  "got.dispatch",
  "got.print_sample",
  "got.result_update",
  "got.review",
  "got.final_grade",
  "got.biography",
  // One code per GOT report (user, 2026-10-16), shown under Reports ▸ GOT: `got.reports` is the
  // QC-App's four pending reports (the code the handheld already checks), the rest the web reports
  // by their legacy menu key.
  "got.reports",
  "got.reports.sample_dispatch",
  "got.reports.pending",
  "got.reports.daily_result",
  "got.reports.result",
  "got.reports.periodical_result_qcs",
  "got.reports.periodical_result_qcm",
  "got.reports.sowing_pending",
  "got.reports.transplantation_pending",
  "got.reports.data_pending",
  "got.reports.prod_person_status",
  "got.reports.final_grade",
  "got.reports.data_in_terra",
  "got.reports.data_in_situ",
  "got.reports.qcb_sowing_pending",
  "got.reports.qcb_transplant_pending",
  "got.reports.qcb_observation_pending",
  "got.reports.qcb_biotech",
  "got.reports.qcb_periodical",
  "got.reports.qcb_data",
  "got.locations",
  "got.receipt",
  "got.allocation",
  "got.sowing",
  "got.transplant",
  "got.fmv",
  "got.observation",
  "got.final_submit",
  "got.bts",
  // PPN + Resow (user, 2026-10-22; docs/designs/got-ppn-resow.md): the handheld counts the plants
  // that stood against the crop's Standard PPN; below it, a supervisor decides on the web (keep,
  // resow keeping R1, discard R1 and resow from held seed, or a new QC sample).
  "got.ppn",
  "got.resow_decide",
  // QC → GOT sample hand-over (docs/designs/got-qc-handover.md): QC sends samples on a numbered GHN
  // note (and prints / cancels it); the GOT team marks what physically arrived. No role holds them
  // by default: admins map them in the access matrix (user, 2026-10-09).
  "got.handover_send",
  "got.handover_receive",
  // the warehouse tree and the variety-pair restrictions are masters to the operator but are their
  // own screens rather than registry CRUD, so they carry their own codes
  "locations.view",
  "locations.manage",
  "storage_restrictions.view",
  "storage_restrictions.manage",
  // Mail Settings (user, 2026-10-15): each plant's own mail sender. `manage` adds, edits and sends a
  // test mail; the stored passwords and tokens are never shown to anyone, `manage` included.
  "mail_settings.view",
  "mail_settings.manage",
  // API Feed Data (user, 2026-10-15): read-only view of every lot the S-Capture / COB / Core PDN
  // feeds sent (pdn_inbox), raw payloads included.
  "pdn_inbox.view",
  // inventory: the read-only stock view (storage and floating), built on the ledger's balances
  "inventory.view",
  // Stock Reconciliation: the ledger-vs-balance check, "Run now", and repairing a balance to its
  // ledger sum.
  "inventory.reconcile",
  // Loading a plant's opening stock from a SeedTrac sheet, and cancelling a load nothing has used.
  "inventory.opening",
  // Reserving whole lots for an export order, and releasing them (no approval; user 2026-09-30).
  "inventory.reserve",
  // Orders (docs/designs/dispatch-module.md, user 2026-10-30): SeedTrac's Order Booking login.
  // `view` opens the orders and their status; `book` adds and edits a draft; `approve` releases
  // it to Dispatch; `hold` holds and unholds a variety, party or order; `cancel` cancels or
  // short-closes one. The dispatches themselves are documents, on their forms' grants.
  "orders.view",
  "orders.book",
  "orders.approve",
  "orders.hold",
  "orders.cancel",
  // One code per Stock Report (user, 2026-10-05); the code alone opens it (2026-10-16).
  "inventory.reports.ageing",
  "inventory.reports.summary",
  "inventory.reports.qc_validity",
  // Reports > Stock (stock-reports.ts, user 2026-10-23): one code per report, the code alone opens it.
  "stock.reports.warehouse_wise",
  "stock.reports.dogr",
  "stock.reports.treatment",
  "stock.reports.germination_got",
  // The Warehouse desk (SeedTrac's RSW / CSW / PSW; docs/designs/rsw-warehouse-module.md): `view`
  // opens its lists, `seed_status` puts lots on or off Drying / Fumigation / Quarantine.
  "warehouse.view",
  "warehouse.seed_status",
  // SLOC Updation, one toggle per desk (user, 2026-10-13): "RSW SLOC Updation" alone opens and works
  // SLOC at RSW on the web and the handheld -- view, pick, place, close and cancel. No other code
  // gates SLOC; the parent `warehouse.sloc` was retired into these (migration 0110).
  "warehouse.sloc.raw",
  "warehouse.sloc.condition",
  "warehouse.sloc.pack",
  // Which desks a role works: every warehouse code above acts only on the stages held here, and a
  // holder of any desk code moves only those stages' lots on handheld SLOC Updation too (user,
  // 2026-10-09: RSW / CSW / PSW logins each locked to their own desk).
  "warehouse.desk.raw",
  "warehouse.desk.condition",
  "warehouse.desk.pack",
  // Raw Seed Release (raw-seed-release.ts): `release` takes undecoded raw seed out of stock for
  // trial use; `release.cancel` reverses a release that did not go out.
  "warehouse.release",
  "warehouse.release.cancel",
  // CSW Batch Creation (batch-creation.ts): cuts a Condition lot's next batch into a SLOC.
  "warehouse.batch",
  // The RSW reports (warehouse-reports.ts): read-only, CSV and PDF included. Each report's own code
  // alone opens it from the Reports menu, with no desk or Warehouse view (user, 2026-10-16).
  "warehouse.reports.quality",
  "warehouse.reports.crop_variety",
  "warehouse.reports.substandard",
  "warehouse.reports.unidentified",
  "warehouse.reports.coded",
  "warehouse.reports.reserve",
  "warehouse.reports.warehouse_wise",
  "warehouse.reports.daily_qc",
  "warehouse.reports.daily_got",
  "warehouse.reports.qc_ageing",
  "warehouse.reports.periodical_got",
  // The CSW reports (CSW_REPORTS in warehouse-reports.ts), one code each, opened from the Reports
  // menu on the code alone like the RSW ones (user, 2026-10-16).
  "csw.reports.quality",
  "csw.reports.crop_variety",
  "csw.reports.substandard",
  "csw.reports.reserve",
  "csw.reports.warehouse_wise",
  "csw.reports.daily_qc",
  "csw.reports.qc_ageing",
  "csw.reports.daily_got",
  // Stores (stores.ts): the consumables store after seedtrac STORES. `view` opens the module and its
  // lists; one code per Arrival kind raises, edits, previews and submits it; `arrival.cancel`
  // reverses a submitted arrival's stock (with the e-mailed code).
  "stores.view",
  "stores.arrival.vendor",
  "stores.arrival.stock_transfer",
  "stores.arrival.return",
  "stores.arrival.cancel",
  // Stores outbound and in-store moves (stores-issue.ts). One code per screen raises, edits and
  // submits it; `issue.cancel` reverses any submitted outbound, SLOC or count (with the code).
  "stores.issue.internal",
  "stores.issue.stock_transfer",
  "stores.issue.return_vendor",
  "stores.captive",
  "stores.discard",
  "stores.issue.cancel",
  "stores.sloc",
  // Cycle Inventory, after Physical Verification: `count` counts blind, `count.approve` sees the
  // book and posts the differences. Never the same person.
  "stores.count",
  "stores.count.approve",
  // Order Placement at Reorder: records that an order went out for an item at its level.
  "stores.reorder",
  // Raising an e-Indent: anyone on the floor who needs items from the store.
  "stores.indent.raise",
  // Stores corrections (stores-corrections.ts), seedtrac's admin-only Transactions: `correct` raises
  // and Final Submits Good to Damage, Damage to Good, Excess/Shortage and Inter Item Transfer in one
  // step (user, 2026-10-07); `correct.cancel` reverses a posted one (with the e-mailed code);
  // `opening` loads a plant's Opening Stock sheet and cancels a load.
  "stores.correct",
  "stores.correct.cancel",
  "stores.opening",
  // One code per Stores Report; the code alone opens it (2026-10-16).
  "stores.reports.stock_on_hand",
  "stores.reports.party_wise",
  "stores.reports.item_ledger",
  "stores.reports.stock_transfer",
  "stores.reports.captive",
  "stores.reports.discard",
  "stores.reports.reorder_level",
  "stores.reports.sloc_status",
  "stores.reports.consumption",
  "stores.reports.periodical_arrival",
  // Priming and Thermal hand-off (user, 2026-10-29): Processing issues and puts away (SLOC); R&D and
  // the Thermal team receive the lot and fill the slip (processing-handoff.ts).
  "processing.priming.issue",
  "processing.priming.receive",
  "processing.priming.slip",
  "processing.priming.sloc",
  "processing.thermal.issue",
  "processing.thermal.receive",
  "processing.thermal.slip",
  "processing.thermal.sloc",
  // One code per Processing Report (user, 2026-10-05); the code alone opens it (2026-10-16).
  "processing.reports.processing",
  "processing.reports.drying",
  "processing.reports.priming",
  "processing.reports.color_sort",
  "processing.reports.consolidated",
  "processing.reports.got_ok",
  "processing.reports.periodical_got",
  // The Processing login's status reports (manual p327–332, 2026-10-09).
  "processing.reports.organiser_status",
  "processing.reports.personnel_status",
  "processing.reports.qc_ageing",
  // Packaging's own card on Roles & Permissions (user, 2026-11-02): the handheld packing floor
  // (Pack line, Manual, Re-printing, Replace) and the Packing Kiosk are separate ticks, so a kiosk
  // login holds the kiosk alone. The floor still needs its `document.*` + Packing Slip grants too.
  "packaging.handheld",
  "packaging.kiosk",
  // One code per Packaging Report, the legacy packging/ Reports menu (user, 2026-10-22).
  "packaging.reports.stock",
  "packaging.reports.daily_qc",
  "packaging.reports.qc_ageing",
  "packaging.reports.activity",
  "packaging.reports.variety_wise",
  "packaging.reports.packing",
  "packaging.reports.ivt",
  // admin
  "users.manage",
  "roles.manage",
  "integrations.manage",
  // Feedback (temporary, dev-phase client tracker; docs/designs/feedback-module.md). `view` opens the
  // module and covers everything on items the user is involved in; `create` only raises new items;
  // `manage` is the tech team: sees every item, moves status, assigns owners. In no role seed —
  // granted by scripts/grant-feedback.ts and the Roles screen. Removed with the module.
  "feedback.view",
  "feedback.create",
  "feedback.manage",
  // the cross-module audit log. One record's history needs only that record's own view code.
  "audit.view",
  // Activity & Audit ▸ Activity: who was signed in and working on which day, from the sessions
  // refresh chain (docs/designs/activity-audit-hub.md). Separate from `audit.view` so a plant head can
  // see presence without every change's before/after.
  "audit.activity.view",
  // Activity & Audit ▸ Security: refresh-token reuse, failed sign-in bursts, new devices and access
  // changes. Kept apart so who may watch for break-ins is a deliberate grant.
  "audit.security.view",
  // Activity & Audit ▸ Retention: purge old months of a log. Needs an e-mailed code every time
  // (STEP_UP_PERMISSIONS) and leaves a permanent log_purges row.
  "audit.purge",
  // Act on a step-up permission without the e-mailed code (user, 2026-10-15): prod mail cannot
  // leave the droplet, so the code never arrives. Admin only; the reason and audit row still apply.
  "auth.step_up.skip",
  // The top strip's Help pages (Abbreviations). Every seeded role but the guard's (user, 2026-09-30).
  "help.view",
  // Reports menu (user, 2026-10-15): the Arrival Report, every arrival form and stage at a plant,
  // not tied to a warehouse desk.
  "reports.arrival",
  // Reports > Variety Decoding, its own tick since 2026-10-16 (it rode on reports.arrival before).
  "reports.variety_decoding",
  // Reports > High Moisture Lots (user, 2026-10-27): arrival lots read above standard, until dried.
  "reports.high_moisture",
  "access_all_facilities",
] as const;

/**
 * Registry masters are gated per screen and per verb, so an admin can grant Vendors without
 * granting Varieties. The codes are derived from MASTER_KEYS rather than written out: adding a
 * master to the registry adds its four codes, and `registerCrud` reads the same four off the spec,
 * so the list and the enforcement cannot drift apart.
 */
export const MASTER_VERBS = ["view", "add", "edit", "delete"] as const;
export type MasterVerb = (typeof MASTER_VERBS)[number];
export type MasterPermission = `masters.${MasterKey}.${MasterVerb}`;

export const masterPermission = (
  k: MasterKey,
  v: MasterVerb
): MasterPermission => `masters.${k}.${v}`;

/** The four codes for one master, in the shape `MasterSpec.permission` and `CrudSpec` both want. */
export const masterPermissions = (k: MasterKey) => ({
  view: masterPermission(k, "view"),
  add: masterPermission(k, "add"),
  edit: masterPermission(k, "edit"),
  delete: masterPermission(k, "delete"),
});

const MASTER_PERMISSIONS: MasterPermission[] = MASTER_KEYS.flatMap((k) =>
  MASTER_VERBS.map((v) => masterPermission(k, v))
);

export type Permission = (typeof BASE_PERMISSIONS)[number] | MasterPermission;

/** Every Priming and Thermal hand-off step: Processing's roles hold them all. */
const HANDOFF_CODES = BASE_PERMISSIONS.filter((p) =>
  /^processing\.(priming|thermal)\.(issue|receive|slip|sloc)$/.test(p)
);

export const PERMISSIONS: readonly Permission[] = [
  ...BASE_PERMISSIONS,
  ...MASTER_PERMISSIONS,
];

export const ROLE_CODES = [
  "security_guard",
  "qa_sampling_executive",
  "qa_analyst",
  "qa_manager",
  "qa_supervisor",
  "got_field_executive",
  "got_reviewer",
  "warehouse_operator",
  "processing_supervisor",
  "rnd_priming",
  "thermal_operator",
  "packing_supervisor",
  "packaging_kiosk",
  "dispatch_clerk",
  "plant_head",
  "stores_operator",
  "stores_manager",
  "admin",
] as const;
export type RoleCode = (typeof ROLE_CODES)[number];

/**
 * Seeded role → permission sets (C23). Editable in the admin UI; this is the starting point.
 *
 * Several of these now hold identical code sets — the three QA roles, and processing / packing /
 * dispatch. That is not a mistake and not a merge candidate: they are told apart by their per-form
 * grants in `role_form_permissions`, which is the axis the arrival engine actually branches on, and
 * by the module codes each will gain when its own screens ship.
 */
/**
 * The Dashboard tab each code opens, and the module code that tab borrowed before it had its own.
 * Seeds and the 0076 backfill both grant a tab to whoever held dashboard.view plus that module code,
 * so nobody gains or loses a tab in the switch.
 */
export const DASHBOARD_TAB_FROM = {
  "dashboard.overview": "document.view",
  "dashboard.gate": "gate.view",
  "dashboard.arrival": "document.view",
  "dashboard.quality": "qc.view",
  "dashboard.processing": "document.view",
  "dashboard.packaging": "document.view",
  "dashboard.stores": "stores.view",
} as const satisfies Partial<Record<Permission, Permission>>;

/**
 * Each report's own code, the code that opened it before it had one (what the 0079 backfill and
 * `withTabs` grant it from), and its name in the Roles editor.
 */
/**
 * A code that once opened a whole report page and was retired into the per-report codes (0120,
 * 2026-10-16). Kept only as `from` sources, so seeds and tests written against them still expand.
 */
export type RetiredReportGate = "warehouse.reports";

export const REPORT_PERMISSIONS = {
  "reports.arrival": { from: "reports.arrival", label: "Arrival Report" },
  "reports.variety_decoding": {
    from: "reports.arrival",
    label: "Variety Decoding",
  },
  "reports.high_moisture": {
    from: "qc.moisture_alert",
    label: "High Moisture Lots",
  },
  "warehouse.reports.quality": {
    from: "warehouse.reports",
    label: "Quality Based Raw Seed Stock",
  },
  "warehouse.reports.crop_variety": {
    from: "warehouse.reports",
    label: "Crop Variety wise Raw Seed",
  },
  "warehouse.reports.substandard": {
    from: "warehouse.reports",
    label: "Crop Variety wise Substandard Raw Seed",
  },
  "warehouse.reports.coded": {
    from: "warehouse.reports",
    label: "Coded Raw Seed",
  },
  "warehouse.reports.reserve": {
    from: "warehouse.reports",
    label: "Reserve Seed",
  },
  "warehouse.reports.daily_qc": {
    from: "warehouse.reports",
    label: "Daily QC Result",
  },
  "warehouse.reports.periodical_got": {
    from: "warehouse.reports",
    label: "Periodical GOT",
  },
  "warehouse.reports.unidentified": {
    from: "warehouse.reports",
    label: "Unidentified Raw Seed",
  },
  "warehouse.reports.warehouse_wise": {
    from: "warehouse.reports",
    label: "Warehouse wise (RSW)",
  },
  "warehouse.reports.daily_got": {
    from: "warehouse.reports",
    label: "Daily GOT Result (RSW)",
  },
  "warehouse.reports.qc_ageing": {
    from: "warehouse.reports",
    label: "QC Ageing Status (RSW)",
  },
  "csw.reports.quality": {
    from: "warehouse.reports",
    label: "Quality Based Condition Seed",
  },
  "csw.reports.crop_variety": {
    from: "warehouse.reports",
    label: "Crop Variety wise Condition Seed",
  },
  "csw.reports.substandard": {
    from: "warehouse.reports",
    label: "Substandard Condition Seed",
  },
  "csw.reports.reserve": {
    from: "warehouse.reports",
    label: "Reserve Condition Seed",
  },
  "csw.reports.warehouse_wise": {
    from: "warehouse.reports",
    label: "Warehouse wise (CSW)",
  },
  "csw.reports.daily_qc": {
    from: "warehouse.reports",
    label: "Daily QC Result (CSW)",
  },
  "csw.reports.qc_ageing": {
    from: "warehouse.reports",
    label: "QC Ageing Status (CSW)",
  },
  "csw.reports.daily_got": {
    from: "warehouse.reports",
    label: "Daily GOT Result (CSW)",
  },
  "inventory.reports.ageing": { from: "inventory.view", label: "Stock Ageing" },
  "inventory.reports.summary": {
    from: "inventory.view",
    label: "Stock Summary",
  },
  "inventory.reports.qc_validity": {
    from: "inventory.view",
    label: "QC Validity",
  },
  // Reports > Stock: whoever saw the Inventory stock reports.
  "stock.reports.warehouse_wise": {
    from: "inventory.view",
    label: "Warehouse Wise Stock",
  },
  "stock.reports.dogr": {
    from: "inventory.view",
    label: "Veg. Seed Stock DOGR",
  },
  "stock.reports.treatment": {
    from: "inventory.view",
    label: "Treated & Untreated",
  },
  "stock.reports.germination_got": {
    from: "inventory.view",
    label: "Germination & GOT Status",
  },
  "processing.reports.processing": {
    from: "document.view",
    label: "Periodical Processing",
  },
  "processing.reports.drying": {
    from: "document.view",
    label: "Periodical Drying",
  },
  "processing.reports.priming": {
    from: "document.view",
    label: "Priming Register",
  },
  "processing.reports.color_sort": {
    from: "document.view",
    label: "Periodical Color Sorting",
  },
  "processing.reports.got_ok": {
    from: "processing.reports.processing",
    label: "GOT OK Report",
  },
  "processing.reports.periodical_got": {
    from: "processing.reports.processing",
    label: "Periodical GOT Report",
  },
  "processing.reports.consolidated": {
    from: "document.view",
    label: "Consolidated",
  },
  "processing.reports.organiser_status": {
    from: "document.view",
    label: "Organiser wise Processing Status",
  },
  "processing.reports.personnel_status": {
    from: "document.view",
    label: "Production Personnel wise Processing Status",
  },
  "processing.reports.qc_ageing": {
    from: "document.view",
    label: "QC Test Ageing Status",
  },
  // Packaging Reports: every role on the Packaging dashboard had them on SeedTrac's packging login.
  "packaging.reports.stock": {
    from: "dashboard.packaging",
    label: "Pack Seed Stock",
  },
  "packaging.reports.daily_qc": {
    from: "dashboard.packaging",
    label: "Daily QC Result (PSW)",
  },
  "packaging.reports.qc_ageing": {
    from: "dashboard.packaging",
    label: "QC Ageing Status (PSW)",
  },
  "packaging.reports.activity": {
    from: "dashboard.packaging",
    label: "Pack Seed Activity",
  },
  "packaging.reports.variety_wise": {
    from: "dashboard.packaging",
    label: "Variety wise Pack Seed",
  },
  "packaging.reports.packing": {
    from: "dashboard.packaging",
    label: "Periodical Packing",
  },
  "packaging.reports.ivt": {
    from: "dashboard.packaging",
    label: "Periodical IVT",
  },
  "stores.reports.stock_on_hand": {
    from: "stores.view",
    label: "Stock on Hand",
  },
  "stores.reports.party_wise": {
    from: "stores.view",
    label: "Party wise Stock",
  },
  "stores.reports.item_ledger": {
    from: "stores.view",
    label: "Stores Item Ledger",
  },
  "stores.reports.stock_transfer": {
    from: "stores.view",
    label: "Stores Stock Transfer",
  },
  "stores.reports.captive": {
    from: "stores.view",
    label: "Captive Consumption",
  },
  "stores.reports.discard": { from: "stores.view", label: "Discard" },
  "stores.reports.reorder_level": {
    from: "stores.view",
    label: "Reorder Level",
  },
  "stores.reports.sloc_status": { from: "stores.view", label: "SLOC Status" },
  "stores.reports.consumption": { from: "stores.view", label: "Consumption" },
  "stores.reports.periodical_arrival": {
    from: "stores.view",
    label: "Periodical Arrival",
  },
  "got.reports": {
    from: "got.reports",
    label: "GOT handheld reports (QC-App)",
  },
  "got.reports.sample_dispatch": {
    from: "got.reports",
    label: "GOT Sample Dispatch Report",
  },
  "got.reports.pending": { from: "got.reports", label: "Pending GOT Report" },
  "got.reports.daily_result": {
    from: "got.reports",
    label: "Daily GOT Result Report",
  },
  "got.reports.result": { from: "got.reports", label: "GOT Result Report" },
  "got.reports.periodical_result_qcs": {
    from: "got.reports",
    label: "Periodical GOT Result Report (QCS)",
  },
  "got.reports.periodical_result_qcm": {
    from: "got.reports",
    label: "Periodical GOT Result Report (QCM)",
  },
  "got.reports.sowing_pending": {
    from: "got.reports",
    label: "GOT Sowing Pending Report",
  },
  "got.reports.transplantation_pending": {
    from: "got.reports",
    label: "Lot Transplantation Pending Report",
  },
  "got.reports.data_pending": {
    from: "got.reports",
    label: "GOT Data Pending Report",
  },
  "got.reports.prod_person_status": {
    from: "got.reports",
    label: "Prod Per Wise GOT Status Report",
  },
  "got.reports.final_grade": {
    from: "got.reports",
    label: "GOT Final Grade Report",
  },
  "got.reports.data_in_terra": {
    from: "got.reports",
    label: "GOT Data Report (In Terra)",
  },
  "got.reports.data_in_situ": {
    from: "got.reports",
    label: "GOT Data Report (In SITU)",
  },
  "got.reports.qcb_sowing_pending": {
    from: "got.reports",
    label: "GOT Sowing Pending Report (QCB)",
  },
  "got.reports.qcb_transplant_pending": {
    from: "got.reports",
    label: "GOT Transplant Pending Report (QCB)",
  },
  "got.reports.qcb_observation_pending": {
    from: "got.reports",
    label: "GOT Observation Pending Report (QCB)",
  },
  "got.reports.qcb_biotech": {
    from: "got.reports",
    label: "GOT Biotech Test Initiative Report (QCB)",
  },
  "got.reports.qcb_periodical": {
    from: "got.reports",
    label: "GOT Report (QCB)",
  },
  "got.reports.qcb_data": {
    from: "got.reports",
    label: "GOT Data Report (QCB)",
  },
  // Reports > Quality: whoever worked in QC (0178 granted them to roles with a QC code beyond View).
  "qc.reports.sample_pending": {
    from: "qc.view",
    label: "QC Sample Pending List",
  },
  "qc.reports.result_pending": {
    from: "qc.view",
    label: "QC Result Pending Report",
  },
  "qc.reports.germ_first_count_list": {
    from: "qc.view",
    label: "Daily 1st Count List",
  },
  "qc.reports.germ_evaluation_list": {
    from: "qc.view",
    label: "Daily Evaluation List",
  },
  "qc.reports.substandard": {
    from: "qc.view",
    label: "Crop Variety wise Substandard Seed Report",
  },
  "qc.reports.soft_release": { from: "qc.view", label: "Soft Release Report" },
  "qc.reports.super_soft_release": {
    from: "qc.view",
    label: "Super Soft Release Report",
  },
  "qc.reports.blending": {
    from: "qc.view",
    label: "Periodical Blending Report",
  },
  "qc.reports.stock": { from: "qc.view", label: "Stock Report" },
  "qc.reports.sample_collection": {
    from: "qc.view",
    label: "Sample Collection Report",
  },
  "qc.reports.stock_status": { from: "qc.view", label: "Stock Status Report" },
  "qc.reports.density": { from: "qc.view", label: "Periodical Density Report" },
  "qc.reports.testing_data": {
    from: "qc.view",
    label: "Periodical Testing Data Report",
  },
  "qc.reports.pp_testing": {
    from: "qc.view",
    label: "Periodical PP Testing Data Report",
  },
  "qc.reports.moisture_testing": {
    from: "qc.view",
    label: "Periodical Moisture Testing Data Report",
  },
  "qc.reports.periodical_arrival": {
    from: "qc.view",
    label: "Periodical Arrival Report",
  },
  "qc.reports.daily_arrival": {
    from: "qc.view",
    label: "Daily Arrival (Key reports)",
  },
  "qc.reports.periodical_dispatch": {
    from: "qc.view",
    label: "Periodical Dispatch Report",
  },
  "dispatch.reports.pending_orders": {
    from: "orders.view",
    label: "Pending Order Report",
  },
  "dispatch.reports.booked_orders": {
    from: "orders.view",
    label: "Periodical Booked Order Report",
  },
  "dispatch.reports.cancelled_orders": {
    from: "orders.view",
    label: "Cancel Order Report",
  },
  "dispatch.reports.suspended_orders": {
    from: "orders.view",
    label: "Suspended Order Report",
  },
  "dispatch.reports.dc_wise": {
    from: "orders.view",
    label: "Periodical Dispatch Report - DC wise",
  },
  "dispatch.reports.crop_variety": {
    from: "orders.view",
    label: "Consolidated CV wise Dispatch Report",
  },
  "dispatch.reports.cnf_barcodes": {
    from: "orders.view",
    label: "CV wise C&F Dispatch Report",
  },
  "dispatch.reports.pack_activity": {
    from: "orders.view",
    label: "Pack Seed Activity Report",
  },
  "dispatch.reports.pack_ageing": {
    from: "orders.view",
    label: "Variety wise Pack Seed Report",
  },
} as const satisfies Partial<
  Record<Permission, { from: Permission | RetiredReportGate; label: string }>
>;

export type ReportPermission = keyof typeof REPORT_PERMISSIONS;

/** One report screen's codes, in screen order: `reportPermissionsOf("inventory")`. */
export const reportPermissionsOf = (
  module:
    | "warehouse"
    | "csw"
    | "inventory"
    | "processing"
    | "packaging"
    | "stock"
    | "stores"
    | "got"
    | "qc"
    | "dispatch"
): ReportPermission[] =>
  (Object.keys(REPORT_PERMISSIONS) as ReportPermission[]).filter(
    (c) => c === `${module}.reports` || c.startsWith(`${module}.reports.`)
  );

/** Every report code: the Reports menu shows to a login holding any of them. */
export const ALL_REPORT_PERMISSIONS = Object.keys(
  REPORT_PERMISSIONS
) as ReportPermission[];

/** A web GOT report's code, by its legacy key (`final-grade` → `got.reports.final_grade`). */
export const GOT_REPORT_PERMISSION = {
  "sample-dispatch": "got.reports.sample_dispatch",
  pending: "got.reports.pending",
  "daily-result": "got.reports.daily_result",
  result: "got.reports.result",
  "periodical-result-qcs": "got.reports.periodical_result_qcs",
  "periodical-result-qcm": "got.reports.periodical_result_qcm",
  "sowing-pending": "got.reports.sowing_pending",
  "transplantation-pending": "got.reports.transplantation_pending",
  "data-pending": "got.reports.data_pending",
  "prod-person-status": "got.reports.prod_person_status",
  "final-grade": "got.reports.final_grade",
  "data-in-terra": "got.reports.data_in_terra",
  "data-in-situ": "got.reports.data_in_situ",
  "qcb-sowing-pending": "got.reports.qcb_sowing_pending",
  "qcb-transplant-pending": "got.reports.qcb_transplant_pending",
  "qcb-observation-pending": "got.reports.qcb_observation_pending",
  "qcb-biotech": "got.reports.qcb_biotech",
  "qcb-periodical": "got.reports.qcb_periodical",
  "qcb-data": "got.reports.qcb_data",
} as const satisfies Record<string, Permission>;
export type GotWebReportKey = keyof typeof GOT_REPORT_PERMISSION;
/** The web GOT reports' codes: the GOT Reports page shows to a login holding any of them. */
export const GOT_WEB_REPORT_PERMISSIONS = Object.values(GOT_REPORT_PERMISSION);

/** A Quality report's code, by its slug (`stock-status` → `qc.reports.stock_status`). */
export const QUALITY_REPORT_PERMISSION = {
  "sample-pending": "qc.reports.sample_pending",
  "result-pending": "qc.reports.result_pending",
  "germ-first-count-list": "qc.reports.germ_first_count_list",
  "germ-evaluation-list": "qc.reports.germ_evaluation_list",
  substandard: "qc.reports.substandard",
  "soft-release": "qc.reports.soft_release",
  "super-soft-release": "qc.reports.super_soft_release",
  blending: "qc.reports.blending",
  stock: "qc.reports.stock",
  "sample-collection": "qc.reports.sample_collection",
  "stock-status": "qc.reports.stock_status",
  density: "qc.reports.density",
  "testing-data": "qc.reports.testing_data",
  "pp-testing": "qc.reports.pp_testing",
  "moisture-testing": "qc.reports.moisture_testing",
  "periodical-arrival": "qc.reports.periodical_arrival",
} as const satisfies Record<QualityReportSlug, Permission>;

/** A Warehouse report's code, by its registry key (`crop-variety` → `warehouse.reports.crop_variety`). */
export const WAREHOUSE_REPORT_PERMISSION = {
  quality: "warehouse.reports.quality",
  "crop-variety": "warehouse.reports.crop_variety",
  substandard: "warehouse.reports.substandard",
  unidentified: "warehouse.reports.unidentified",
  coded: "warehouse.reports.coded",
  reserve: "warehouse.reports.reserve",
  "warehouse-wise": "warehouse.reports.warehouse_wise",
  "daily-qc": "warehouse.reports.daily_qc",
  "daily-got": "warehouse.reports.daily_got",
  "qc-ageing": "warehouse.reports.qc_ageing",
  "periodical-got": "warehouse.reports.periodical_got",
} as const satisfies Record<WarehouseReportKey, Permission>;

/** A CSW report's code, by its registry key (`csw-crop-variety` → `csw.reports.crop_variety`). */
export const CSW_REPORT_PERMISSION = {
  "csw-quality": "csw.reports.quality",
  "csw-crop-variety": "csw.reports.crop_variety",
  "csw-substandard": "csw.reports.substandard",
  "csw-reserve": "csw.reports.reserve",
  "csw-warehouse-wise": "csw.reports.warehouse_wise",
  "csw-daily-qc": "csw.reports.daily_qc",
  "csw-qc-ageing": "csw.reports.qc_ageing",
  "csw-daily-got": "csw.reports.daily_got",
} as const satisfies Record<CswReportKey, Permission>;

/** A Packaging report's code, by its registry key (`pack-qc-ageing` → `packaging.reports.qc_ageing`). */
export const PACKAGING_REPORT_PERMISSION = {
  "pack-stock": "packaging.reports.stock",
  "pack-daily-qc": "packaging.reports.daily_qc",
  "pack-qc-ageing": "packaging.reports.qc_ageing",
  "pack-activity": "packaging.reports.activity",
  "pack-variety-wise": "packaging.reports.variety_wise",
  "pack-packing": "packaging.reports.packing",
  "pack-ivt": "packaging.reports.ivt",
} as const satisfies Record<PackagingReportKey, Permission>;

/** A Stock report's code, by its registry key (`stock-dogr` → `stock.reports.dogr`). */
export const STOCK_REPORT_PERMISSION = {
  "stock-warehouse-wise": "stock.reports.warehouse_wise",
  "stock-dogr": "stock.reports.dogr",
  "stock-treatment": "stock.reports.treatment",
  "stock-germination-got": "stock.reports.germination_got",
} as const satisfies Record<StockReportKey, Permission>;

/** Each desk's SLOC Updation toggle: "RSW SLOC Updation", "CSW SLOC Updation", "PSW SLOC Updation". */
export const SLOC_DESK_PERMISSION = {
  raw: "warehouse.sloc.raw",
  condition: "warehouse.sloc.condition",
  pack: "warehouse.sloc.pack",
} as const satisfies Record<string, Permission>;
export type SlocDesk = keyof typeof SLOC_DESK_PERMISSION;
export const SLOC_DESKS = Object.keys(SLOC_DESK_PERMISSION) as SlocDesk[];

/** May this login open and work SLOC Updation at a desk: that desk's toggle. */
export const canSlocOn = (can: (p: Permission) => boolean, stage: SlocDesk) =>
  can(SLOC_DESK_PERMISSION[stage]);

/** The desks this login may move stock at, in SeedTrac's order (RSW, CSW, PSW). */
export const slocDesksOf = (can: (p: Permission) => boolean): SlocDesk[] =>
  SLOC_DESKS.filter((d) => canSlocOn(can, d));

/** The lot stage code (stages.code) each desk moves: RSW raw, CSW condition, PSW pack. */
export const SLOC_DESK_STAGE_CODE = {
  raw: "R",
  condition: "C",
  pack: "P",
} as const satisfies Record<SlocDesk, string>;

/** The floor's name for each desk. */
export const SLOC_DESK_NAME = {
  raw: "RSW",
  condition: "CSW",
  pack: "PSW",
} as const satisfies Record<SlocDesk, string>;

/** The desk a lot stage code belongs to; undefined for a lot of no warehouse stage. */
export const slocDeskOfStageCode = (code: string): SlocDesk | undefined =>
  SLOC_DESKS.find((d) => SLOC_DESK_STAGE_CODE[d] === code);

/** The desks a login works on handheld SLOC Updation: the same toggles as the web. */
export const handheldSlocDesks = slocDesksOf;

/** May this login move a lot of this stage code on the handheld? A lot of no desk belongs to none. */
export function handheldSlocAllows(
  can: (p: Permission) => boolean,
  stageCode: string
): boolean {
  const desk = slocDeskOfStageCode(stageCode);
  return desk === undefined || handheldSlocDesks(can).includes(desk);
}

export const GATE_TAB_PERMISSIONS = [
  "gate.tab.all",
  "gate.tab.inward_pending",
  "gate.tab.inward_done",
  "gate.tab.outward_pending",
  "gate.tab.outward_done",
] as const satisfies readonly Permission[];

/**
 * A permission set plus the dashboard tabs, gate tabs and reports it implies, derived as the 0076
 * and 0079 backfills derived them: what seeds use, and what tests use to say "a role as it was
 * before tabs and reports had codes".
 */
export function withTabs(
  ...p: (Permission | RetiredReportGate)[]
): Permission[] {
  const out = new Set<string>(p);
  if (out.has("dashboard.view"))
    for (const [tab, from] of Object.entries(DASHBOARD_TAB_FROM))
      if (out.has(from)) out.add(tab);
  if (out.has("gate.view")) for (const t of GATE_TAB_PERMISSIONS) out.add(t);
  for (const [code, r] of Object.entries(REPORT_PERMISSIONS))
    if (out.has(r.from)) out.add(code);
  out.delete("warehouse.reports");
  return [...out] as Permission[];
}

/** The dashboard codes each QA role keeps: its own dashboard and nothing withTabs derives. */
const QA_MANAGER_TABS = new Set<string>([
  "dashboard.view",
  "dashboard.qa_manager",
]);
const QA_SUPERVISOR_TABS = new Set<string>([
  "dashboard.view",
  "dashboard.qa_supervisor",
]);

/** What every QA role does in GOT (0083 backfills the same): the QC-App screens, print, biography, reports. */
const GOT_FLOOR: Permission[] = [
  "got.print_sample",
  "got.biography",
  "got.reports",
  "got.receipt",
  "got.allocation",
  "got.sowing",
  "got.transplant",
  "got.ppn",
  "got.fmv",
  "got.observation",
  "got.final_submit",
  "got.bts",
];

export const ROLE_SEEDS: Record<
  RoleCode,
  { name: string; permissions: Permission[] }
> = {
  // The guard passes trucks in and sees nothing else: no document, no inventory, no master.
  security_guard: {
    name: "Security Guard",
    permissions: withTabs(
      "gate.view",
      "gate.inward",
      "gate.pass_in",
      "gate.outward"
    ),
  },
  // The three QA roles read arrivals, and the QC module splits them apart (C23): the executive
  // raises sampling requests, draws and collects samples; the analyst enters results; the manager
  // only reads until dispositions have a screen.
  qa_sampling_executive: {
    name: "QA Sampling Executive",
    permissions: withTabs(
      "help.view",
      "dashboard.view",
      "inventory.view",
      "document.view",
      "qc.view",
      "qc.dashboard",
      "qc.sample_pending",
      "qc.request",
      "qc.generation",
      "qc.collect",
      "qc.guard_samples",
      "qc.lot_biography",
      "qc.dov_calculator",
      "qc.barcode_search",
      "qc.batch_finder",
      "got.handover_send",
      "qc.allocation_view",
      "got.dispatch",
      ...GOT_FLOOR
    ),
  },
  qa_analyst: {
    name: "QA Analyst",
    permissions: withTabs(
      "help.view",
      "dashboard.view",
      "inventory.view",
      "document.view",
      "qc.view",
      "qc.dashboard",
      "qc.sample_pending",
      // No test forms by role (user, 2026-10-17): the QC supervisor ticks Moisture / PP /
      // Germination per analyst on the Lab team sheet, as personal grants.
      "qc.result_update",
      "qc.allocation_view",
      "qc.lot_biography",
      "qc.dov_calculator",
      "qc.barcode_search",
      "qc.batch_finder",
      "qc.density",
      ...GOT_FLOOR
    ),
  },
  qa_manager: {
    name: "QA Manager",
    permissions: withTabs(
      "help.view",
      "dashboard.view",
      "inventory.view",
      "document.view",
      "qc.view",
      "qc.dashboard",
      "qc.sample_pending",
      "qc.verify",
      "qc.allocate",
      "qc.allocation_view",
      // The lab's named staff (user, 2026-10-23): kept by whoever allocates the tests.
      "masters.qc-analysts.view",
      "masters.qc-analysts.add",
      "masters.qc-analysts.edit",
      "masters.qc-analysts.delete",
      "qc.germination_setup",
      "qc.soft_release",
      "qc.super_soft_release",
      "qc.guard_samples",
      "qc.lot_biography",
      "qc.dov_calculator",
      "qc.barcode_search",
      "qc.batch_finder",
      "qc.density",
      "qc.density_edit",
      "got.result_update",
      "got.review",
      "got.final_grade",
      "got.locations",
      "got.resow_decide",
      ...GOT_FLOOR,
      // Quarantine is a quality call as much as a warehouse one.
      "warehouse.view",
      "warehouse.desk.raw",
      "warehouse.desk.condition",
      "warehouse.desk.pack",
      "warehouse.seed_status",
      "warehouse.reports",
      "reports.arrival",
      // The QA Manager's dashboard. Its action codes above leave in a second commit, after
      // scripts/revoke-qa-manager.ts has run on prod (docs/designs/qa-dashboard.md D4).
      "dashboard.qa_manager"
      // One dashboard per role (user, 2026-10-21): none of the tabs withTabs derives.
    ).filter((p) => !p.startsWith("dashboard.") || QA_MANAGER_TABS.has(p)),
  },
  // The lab's supervisor (user, 2026-10-20): allocates every test, verifies Moisture, PP and
  // Germination, updates the result and soft-releases. It runs the floor without drawing samples or
  // entering readings itself, so the sampling duty stays with the executives (C23).
  qa_supervisor: {
    name: "QA Supervisor",
    permissions: withTabs(
      "help.view",
      "dashboard.view",
      "dashboard.qa_supervisor",
      "inventory.view",
      "document.view",
      "qc.view",
      "qc.dashboard",
      "qc.sample_pending",
      "qc.standards",
      "qc.allocate",
      "qc.allocation_view",
      "masters.qc-analysts.view",
      "masters.qc-analysts.add",
      "masters.qc-analysts.edit",
      "masters.qc-analysts.delete",
      "qc.verify",
      "qc.germination_setup",
      "qc.result_update",
      "qc.soft_release",
      "qc.super_soft_release",
      "qc.guard_samples",
      "qc.lot_biography",
      "qc.dov_calculator",
      "qc.barcode_search",
      "qc.batch_finder",
      "got.handover_send",
      "qc.density",
      "qc.density_edit"
    ).filter((p) => !p.startsWith("dashboard.") || QA_SUPERVISOR_TABS.has(p)),
  },
  // GOT has its own people (user, 2026-10-20): the field team sows, transplants and observes; the
  // reviewer reads the plots and gives the final grade. QA executives and analysts keep GOT_FLOOR.
  got_field_executive: {
    name: "GOT Field Executive",
    // Lot Biography in Look-up for the GOT roles too (user, 2026-10-21).
    permissions: withTabs(
      "help.view",
      "qc.lot_biography",
      // GOT receives what QC hands over (user, 2026-10-21).
      "got.handover_receive",
      ...GOT_FLOOR
    ),
  },
  got_reviewer: {
    name: "GOT Reviewer",
    permissions: withTabs(
      "help.view",
      "got.result_update",
      "got.review",
      "got.final_grade",
      "got.locations",
      "got.resow_decide",
      "got.biography",
      "got.reports",
      "qc.lot_biography",
      "got.handover_receive"
    ),
  },
  warehouse_operator: {
    name: "Warehouse Operator",
    permissions: withTabs(
      "help.view",
      // Read-only on the gate: the executive must be able to see which trucks were passed in
      // before booking their lots, but the pass itself is the guard's to raise. Which arrival form a
      // typed pass is for is the executive's call.
      "gate.view",
      "gate.assign_arrival",
      "dashboard.view",
      "inventory.view",
      "document.view",
      "document.create",
      "document.edit",
      "document.submit",
      "document.email",
      "warehouse.view",
      "warehouse.desk.raw",
      "warehouse.desk.condition",
      "warehouse.desk.pack",
      "warehouse.seed_status",
      "warehouse.sloc.raw",
      "warehouse.sloc.condition",
      "warehouse.sloc.pack",
      "warehouse.release",
      "warehouse.reports",
      "reports.arrival"
    ),
  },
  processing_supervisor: {
    name: "Processing Supervisor",
    permissions: withTabs(
      "help.view",
      "dashboard.view",
      "inventory.view",
      "document.view",
      "document.create",
      "document.edit",
      "document.submit",
      // Issues to R&D / Thermal and puts away what comes back; may stand in for either section.
      ...HANDOFF_CODES
    ),
  },
  // R&D works the Priming the Processing team issues to it (user, 2026-10-29): receive, slip, send back.
  rnd_priming: {
    name: "R&D Priming",
    permissions: withTabs(
      "help.view",
      "document.view",
      "processing.priming.receive",
      "processing.priming.slip"
    ),
  },
  // The Thermal team, the same for Thermal Treatment.
  thermal_operator: {
    name: "Thermal Operator",
    permissions: withTabs(
      "help.view",
      "document.view",
      "processing.thermal.receive",
      "processing.thermal.slip"
    ),
  },
  packing_supervisor: {
    name: "Packing Supervisor",
    permissions: withTabs(
      "help.view",
      "dashboard.view",
      "inventory.view",
      "orders.view",
      "orders.book",
      "orders.hold",
      "document.view",
      "document.create",
      "document.edit",
      "document.submit",
      // Packaging ▸ Store e-Indents: ask the store for rolls and pouches, send leftovers back
      // (migration 0117 granted it; user 2026-10-30).
      "stores.indent.raise",
      "packaging.handheld",
      "packaging.kiosk"
    ),
  },
  // The pack line's kiosk screen (user, 2026-11-02): the Packing Kiosk and nothing else, no
  // documents, no forms. It opens straight into the kiosk on the web and the handheld.
  packaging_kiosk: {
    name: "Packaging Kiosk",
    permissions: withTabs("help.view", "packaging.kiosk"),
  },
  dispatch_clerk: {
    name: "Dispatch Clerk",
    permissions: withTabs(
      "help.view",
      "dashboard.view",
      "inventory.view",
      "document.view",
      "document.create",
      "document.edit",
      "document.submit"
    ),
  },
  plant_head: {
    name: "Plant Head",
    permissions: withTabs(
      "help.view",
      "gate.view",
      "dashboard.view",
      "inventory.view",
      "orders.view",
      "orders.approve",
      "orders.hold",
      "orders.cancel",
      "document.view",
      "document.cancel",
      "warehouse.view",
      "warehouse.desk.raw",
      "warehouse.desk.condition",
      "warehouse.desk.pack",
      "warehouse.release.cancel",
      "warehouse.reports",
      "reports.arrival",
      ...HANDOFF_CODES,
      // The plant head reads every master but edits none; the codes are derived so a new master
      // does not quietly fall outside their view.
      ...MASTER_KEYS.map((k) => masterPermission(k, "view"))
    ),
  },
  // The store keeper: raises every Arrival and keeps the Stores masters, but cancels nothing.
  stores_operator: {
    name: "Stores Operator",
    permissions: withTabs(
      "help.view",
      "gate.view",
      "stores.view",
      "stores.arrival.vendor",
      "stores.arrival.stock_transfer",
      "stores.arrival.return",
      "stores.issue.internal",
      "stores.issue.stock_transfer",
      "stores.issue.return_vendor",
      "stores.captive",
      "stores.discard",
      "stores.sloc",
      "stores.count",
      "stores.reorder",
      "stores.indent.raise",
      ...STORES_MASTER_KEYS.flatMap((k) =>
        MASTER_VERBS.filter((v) => v !== "delete").map((v) =>
          masterPermission(k, v)
        )
      ),
      // Stores vendors are kept in the one Vendor master.
      masterPermission("vendors", "view"),
      masterPermission("vendors", "add"),
      masterPermission("vendors", "edit")
    ),
  },
  // The Stores Manager (user, 2026-10-14): every Stores screen, including what the operator lacks
  // (count approval, corrections, Opening Stock, the cancels, every report), every Stores master, the
  // shared Vendor and Locations masters in full, and a Dashboard holding only the Stores tab. Counts
  // and approves both (user's choice); approveCount still refuses the person who counted.
  stores_manager: {
    name: "Stores Manager",
    permissions: withTabs(
      "help.view",
      "dashboard.view",
      "dashboard.stores",
      ...BASE_PERMISSIONS.filter((p) => p.startsWith("stores.")),
      ...STORES_MASTER_KEYS.flatMap((k) =>
        MASTER_VERBS.map((v) => masterPermission(k, v))
      ),
      ...MASTER_VERBS.map((v) => masterPermission("vendors", v)),
      "locations.view",
      "locations.manage"
      // No Gate (user, 2026-10-17): the store's login does not work the gate, so its menus are the
      // store's alone. The Dashboard is the Stores tab only.
    ).filter(
      (p) =>
        !p.startsWith("dashboard.") ||
        p === "dashboard.view" ||
        p === "dashboard.stores"
    ),
  },
  admin: { name: "Head Office / Admin", permissions: [...PERMISSIONS] },
};

/** The modules beside the store: holding any code of these makes a login more than a Stores login. */
const SEED_SIDE_MODULES = new Set([
  "csw",
  "document",
  "gate",
  "got",
  "inventory",
  "packaging",
  "pdn_inbox",
  "processing",
  "qc",
  "roles",
  "stock",
  "storage_restrictions",
  "users",
  "warehouse",
]);

/**
 * A login that works the store and nothing else (user, 2026-10-17, the Deorjhal store login): its
 * Masters are the store's masters, its Vendors are the Stores vendors and its Warehouses the Stores
 * racks, on every screen and in the API alike (user, 2026-10-29).
 */
export const isStoresOnly = (can: (p: Permission) => boolean): boolean =>
  can("stores.view") &&
  !PERMISSIONS.some((p) => SEED_SIDE_MODULES.has(p.split(".")[0]!) && can(p));

/**
 * Actions that require an email OTP step-up (C24) in addition to the permission.
 *
 * gstack-shortcut: `users.manage` and `roles.manage` were removed from this set instead of building
 * the web OtpStepUpDialog. Ceiling: the role editor can hand out `document.cancel` without an OTP,
 * while exercising `document.cancel` still demands one. `audit_log` is the compensating control.
 * Upgrade when the dialog ships, or at the first audit finding on admin-account creation.
 */
export const STEP_UP_PERMISSIONS: ReadonlySet<Permission> = new Set<Permission>(
  [
    // Cancel unwinds a posted ledger, which is exactly what step-up is for.
    "document.cancel",
    // Purging a log deletes evidence for good.
    "audit.purge",
    // Same reason as document.cancel: a Stores arrival's cancel reverses posted stock.
    "stores.arrival.cancel",
    "stores.issue.cancel",
    "stores.correct.cancel",
  ]
);
